/* eslint-disable @typescript-eslint/no-unused-vars */
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { isUnifiedStaffCaseIntakeRole } from '@brands/Utils/reviewProfileBasePath';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectStaffCaseCreation } from '@brands/store/selectors/staffCaseCreation';
import React, { FC, useCallback, useEffect, useRef } from 'react';

import styles from './styles.module.scss';

interface ProfileContainerProps {
  children: React.ReactNode;
  containerStyle?: object;
  childClassName?: string;
  childrenStyle?: { [key: string]: string };
  isVisibleOnMobile?: boolean;
  isProfileCard?: boolean;
  consultationPage?: boolean;
  scrollableDashboard?: boolean;
  patientDashboard?: boolean;
  isSidenavVisible?: boolean;
  isProvider?: boolean;
  isIntakeProcess?: boolean;
}

const Container: FC<ProfileContainerProps> = ({
  children,
  containerStyle,
  childClassName,
  childrenStyle,
  isVisibleOnMobile,
  isProfileCard,
  consultationPage,
  scrollableDashboard,
  patientDashboard,
  isSidenavVisible,
  isProvider,
  isIntakeProcess,
}): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const { isStaffIntake } = useAppSelector(selectStaffCaseCreation);

  const staffUnifiedIntake =
    isStaffIntake && userInfo?.role?.name != null && isUnifiedStaffCaseIntakeRole(userInfo.role.name);
  const effectiveSidenavVisible = staffUnifiedIntake ? false : isSidenavVisible;
  const effectiveIsIntakeProcess = staffUnifiedIntake ? true : isIntakeProcess;
  const toastAnchorRef = useRef<HTMLDivElement | null>(null);

  const resetToastAnchor = useCallback((): void => {
    if (typeof document === 'undefined') return;

    const root = document.documentElement;
    root.style.setProperty('--d1-toast-content-center', '50vw');
    root.style.setProperty('--d1-toast-content-left', '0px');
    root.style.setProperty('--d1-toast-content-width', '100vw');
    root.style.setProperty('--d1-toast-available-width', 'calc(100vw - 32px)');
  }, []);

  const updateToastAnchor = useCallback((): void => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;

    const node = toastAnchorRef.current;

    if (!node) {
      resetToastAnchor();
      return;
    }

    const rect = node.getBoundingClientRect();
    const computedStyle = window.getComputedStyle(node);
    const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = Number.parseFloat(computedStyle.paddingRight) || 0;
    const left = Math.min(Math.max(rect.left + paddingLeft, 0), window.innerWidth);
    const contentWidth = Math.max(0, rect.width - paddingLeft - paddingRight);
    const width = Math.max(0, Math.min(contentWidth, window.innerWidth - left));
    const center = left + width / 2;
    const availableWidth = Math.max(0, width - 32);
    const root = document.documentElement;

    root.style.setProperty('--d1-toast-content-center', `${center}px`);
    root.style.setProperty('--d1-toast-content-left', `${left}px`);
    root.style.setProperty('--d1-toast-content-width', `${width}px`);
    root.style.setProperty('--d1-toast-available-width', `${availableWidth}px`);
  }, [resetToastAnchor]);

  const setToastAnchorRef = useCallback(
    (node: HTMLDivElement | null): void => {
      toastAnchorRef.current = node;

      if (node && typeof window !== 'undefined') {
        window.requestAnimationFrame(updateToastAnchor);
      }
    },
    [updateToastAnchor],
  );

  useEffect(() => {
    updateToastAnchor();

    if (typeof window === 'undefined') return undefined;

    const handleResize = (): void => updateToastAnchor();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [effectiveIsIntakeProcess, effectiveSidenavVisible, updateToastAnchor]);

  useEffect(() => resetToastAnchor, [resetToastAnchor]);

  return (
    <div
      className={`${!isProfileCard ? styles.containerClass : styles.profileCardContainer} ${childClassName}`}
      style={{
        height: '100vh',
        ...containerStyle,
      }}
    >
      {effectiveSidenavVisible && <Sidenav isVisibleOnMobile={isVisibleOnMobile} isProfileCard={isProfileCard} />}
      {patientDashboard && (
        <div
          ref={setToastAnchorRef}
          className={styles.patientDashboardPosition}
          style={{
            ...childrenStyle,
          }}
        >
          {children}
        </div>
      )}
      {consultationPage && (
        <div
          ref={setToastAnchorRef}
          className={styles.childrenPositionTop}
          style={{
            ...childrenStyle,
          }}
        >
          {children}
        </div>
      )}
      {scrollableDashboard && (
        <div
          ref={setToastAnchorRef}
          className={styles.childrenScrollableDiv}
          style={{
            ...childrenStyle,
          }}
        >
          {children}
        </div>
      )}

      {!consultationPage && !patientDashboard && !scrollableDashboard && !isProvider && (
        <div
          ref={setToastAnchorRef}
          className={styles.childrenCunsomPosition}
          style={{
            ...childrenStyle,
          }}
        >
          {children}
        </div>
      )}
      {isProvider && (
        <div
          ref={setToastAnchorRef}
          className={styles.childrenProviderCunsomPosition}
          style={{
            ...childrenStyle,
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
Container.defaultProps = {
  containerStyle: {},
  childClassName: '',
  childrenStyle: {},
  isVisibleOnMobile: true,
  isProfileCard: false,
  consultationPage: false,
  scrollableDashboard: false,
  patientDashboard: false,
  isSidenavVisible: true,
  isProvider: false,
  isIntakeProcess: false,
};

export default Container;
